import { useState, useCallback, useEffect } from 'react'

const defaultDebounceTime = 1000
const asyncStatuses = {
  IDLE: 'idle',
  PENDING: 'pending',
  SUCCESS: 'success',
  ERROR: 'error',
}

// minimumDuration accepts a number in ms, else if truthy sets as defaultDebounceTime
// executeOnMount if you just want shit to happen and don't need to fire manually
const useAsync = (asyncFunction, { minimumDuration, executeOnMount } = {}) => {
  const [callCount, setCallCount] = useState(0)
  const [status, setStatus] = useState(asyncStatuses.IDLE)
  const [value, setValue] = useState(null)
  const [error, setError] = useState(null)
  const [lastCallFailed, setLastCallFailed] = useState(null)

  let debounce = 0
  if (typeof minimumDuration === 'number') {
    debounce = minimumDuration
  } else if (minimumDuration) {
    debounce = defaultDebounceTime
  }

  // The execute function wraps asyncFunction and
  // handles setting state for pending, value, and error.
  // useCallback ensures the below useEffect is not called
  // on every render, but only if asyncFunction changes.
  const execute = useCallback(
    (...args) => {
      // used for uniqueness, reset function will increment, thus
      // previous calls will be ignored for returning status
      const callNumber = callCount

      try {
        setStatus(asyncStatuses.PENDING)
        setValue(null)
        setError(null)

        const fireAsync = new Promise((resolve, reject) =>
          asyncFunction(...args)
            .then(resolve)
            .catch(reject)
        )
        const overrideLoading = new Promise((resolve) =>
          setTimeout(resolve, debounce)
        )

        // allSettled to wiat on both async call, and debounceTime
        // (whatever is slowest) if minimumDuration option is true
        return Promise.allSettled([fireAsync, overrideLoading]).then(
          (responseArray) => {
            const result = responseArray[0]
            if (callNumber === callCount) {
              if (result.status === 'rejected') {
                setError(result.reason)
                setStatus(asyncStatuses.ERROR)
                setLastCallFailed(true)
              } else {
                setValue(result.value)
                setStatus(asyncStatuses.SUCCESS)
                setLastCallFailed(false)
              }
            }
          }
        )
      } catch (error) {
        if (callNumber === callCount) {
          setError(error)
          setStatus(asyncStatuses.ERROR)
          setLastCallFailed(true)
        }
        return null
      }
    },
    [asyncFunction, debounce, callCount]
  )

  // resets status back to idle, disregards any result
  // of in progress requests (w/ callCount uniqueuness)
  const reset = useCallback(() => {
    setCallCount(callCount + 1)
    setStatus(asyncStatuses.IDLE)
    setValue(null)
    setError(null)
    setLastCallFailed(null)
  }, [callCount])

  // Call execute if we want to fire it right away.
  // Otherwise execute can be called later, such as
  // in an onClick handler.
  useEffect(() => {
    if (execute && executeOnMount && status === asyncStatuses.IDLE) {
      execute()
    }
  }, [execute, executeOnMount, status])

  return {
    // Functions
    execute, // fires async function provided
    reset, // resets to IDLE, disregard previous call's responses

    // execute reporting namespaced
    executeStatus: status, // one of [asyncStatuses]
    executeValue: value, // result of execute
    executeError: error, // error execute may have thrown

    // actionable statuses
    isIdle: status === asyncStatuses.IDLE,
    isLoading: status === asyncStatuses.PENDING,
    isSuccess: status === asyncStatuses.SUCCESS,
    isErrored: status === asyncStatuses.ERROR,
    promptRetry: lastCallFailed, // previous failure, but retryable until success
  }
}

export default useAsync
